import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  TablePagination,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import RefreshIcon from "@mui/icons-material/Refresh";
import InboxIcon from "@mui/icons-material/Inbox";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import axios from "axios";

const TYPE_COLORS = {
  Reminder: "#ff9800",
  Success:  "#4caf50",
  Alert:    "#f44336",
  Info:     "#2196f3",
};

const MODULE_INITIALS = {
  Fee:        "₹",
  Attendance: "✓",
  Exam:       "📝",
  Chat:       "💬",
  default:    "🔔",
};

export default function ParentNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [pageNo, setPageNo]               = useState(1);
  const [pageSize, setPageSize]           = useState(10);
  const [hasMore, setHasMore]             = useState(false);
  const [loading, setLoading]             = useState(false);
  const [loadingMore, setLoadingMore]     = useState(false);
  const [marking, setMarking]             = useState(false);
  const [tab, setTab]                     = useState(0);
  const listRef                           = useRef(null);

  const BASE  = process.env.REACT_APP_BASE_URL;
  const token = sessionStorage.getItem("token");
  const AUTH  = { Authorization: token };

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE}/NewNotification/GetUnreadCount`, { headers: AUTH });
      setUnreadCount(res.data?.unreadCount ?? 0);
    } catch (e) { console.error(e); }
  }, [BASE]);

  const fetchNotifications = useCallback(async (page = 1, append = false, size = pageSize) => {
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const res   = await axios.get(
        `${BASE}/NewNotification/GetMyNotifications?PageNo=${page}&PageSize=${size}`,
        { headers: AUTH }
      );
      const items = Array.isArray(res.data?.data) ? res.data.data : [];
      setNotifications(prev => append ? [...prev, ...items] : items);
      setHasMore(items.length === size);
    } catch (e) { console.error(e); }
    finally { append ? setLoadingMore(false) : setLoading(false); }
  }, [BASE, pageSize]);

  const markAsRead = async (recipientId) => {
    setNotifications(prev => prev.map(n => n.recipientId === recipientId ? { ...n, isRead: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      await axios.post(`${BASE}/NewNotification/MarkAsRead`, { recipientId }, { headers: AUTH });
    } catch (e) { console.error(e); }
  };

  const markAllAsRead = async () => {
    setMarking(true);
    try {
      await axios.post(`${BASE}/NewNotification/MarkAllAsRead`, {}, { headers: AUTH });
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (e) { console.error(e); }
    finally { setMarking(false); }
  };

  const handleChangePage = (event, newPage) => {
    const next = newPage + 1;
    setPageNo(next);
    fetchNotifications(next, false);
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setPageSize(newSize);
    setPageNo(1);
    fetchNotifications(1, false, newSize);
  };

  useEffect(() => {
    fetchNotifications(1, false);
    fetchUnreadCount();
  }, []);

  const displayed = tab === 1 ? notifications.filter(n => !n.isRead) : notifications;

  const getInitial = (notif) =>
    MODULE_INITIALS[notif.sourceModule] || notif.title?.charAt(0).toUpperCase() || "🔔";

  const formatDate = (dateStr) => {
    if (!dateStr) return "Just now";
    const d       = new Date(dateStr);
    const now     = new Date();
    const diffMs  = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr  = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);
    if (diffMin < 1)  return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr  < 24) return `${diffHr}h ago`;
    if (diffDay < 7)  return `${diffDay}d ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const groupNotifications = (items) => {
    const groups = { Today: [], Yesterday: [], "This Week": [], Older: [] };
    const now    = new Date();
    items.forEach(n => {
      if (!n.createdOn) { groups.Older.push(n); return; }
      const d    = new Date(n.createdOn);
      const diff = Math.floor((now - d) / 86400000);
      if (diff === 0)      groups.Today.push(n);
      else if (diff === 1) groups.Yesterday.push(n);
      else if (diff < 7)   groups["This Week"].push(n);
      else                 groups.Older.push(n);
    });
    return groups;
  };

  const groups = groupNotifications(displayed);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f4f6fb", p: { xs: 1, md: 3 } }}>

      {/* Page Header — blue gradient matching parent Header.jsx */}
      <Box sx={{
        background: "linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%)",
        borderRadius: 3,
        px: 3,
        py: 2,
        mb: 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxShadow: "0 4px 20px rgba(24,40,72,0.25)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <NotificationsIcon sx={{ color: "#fff", fontSize: 28 }} />
          <Box>
            <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}>
              Notifications
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
              Stay updated with latest activity
            </Typography>
          </Box>
          {unreadCount > 0 && (
            <Chip
              label={`${unreadCount} unread`}
              size="small"
              sx={{ bgcolor: "#f44336", color: "#fff", fontWeight: 700, ml: 1 }}
            />
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => { setPageNo(1); fetchNotifications(1, false); fetchUnreadCount(); }}
              sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {unreadCount > 0 && (
            <Tooltip title="Mark all as read">
              <IconButton
                onClick={markAllAsRead}
                disabled={marking}
                sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}
              >
                {marking ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <DoneAllIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ bgcolor: "#fff", borderRadius: 2, mb: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 2,
            "& .MuiTab-root":      { fontWeight: 600, fontSize: "0.85rem", textTransform: "none" },
            "& .Mui-selected":     { color: "#4b6cb7" },
            "& .MuiTabs-indicator":{ bgcolor: "#4b6cb7", height: 3, borderRadius: 2 },
          }}
        >
          <Tab label="All" />
          <Tab label={`Unread${unreadCount > 0 ? ` (${unreadCount})` : ""}`} />
        </Tabs>
      </Box>

      {/* Notification list */}
      <Box
        ref={listRef}
        sx={{
          maxHeight: "calc(100vh - 340px)",
          overflowY: "auto",
          "&::-webkit-scrollbar":       { width: 5 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "#b5c3e8", borderRadius: 3 },
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10 }}>
            <CircularProgress sx={{ color: "#4b6cb7" }} />
          </Box>
        ) : displayed.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <InboxIcon sx={{ fontSize: 64, color: "#e0e0e0", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" fontWeight={600}>
              {tab === 1 ? "No unread notifications" : "You're all caught up!"}
            </Typography>
            <Typography variant="body2" color="text.disabled" mt={0.5}>
              {tab === 1 ? "All notifications have been read." : "No notifications yet."}
            </Typography>
          </Box>
        ) : (
          Object.entries(groups).map(([label, items]) => {
            if (!items.length) return null;
            return (
              <Box key={label} sx={{ mb: 2 }}>

                {/* Group label */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, mb: 1 }}>
                  <Typography variant="caption" sx={{
                    color: "#666", fontWeight: 700, fontSize: "0.72rem",
                    textTransform: "uppercase", letterSpacing: 1,
                  }}>
                    {label}
                  </Typography>
                  <Divider sx={{ flex: 1, borderColor: "#e0e0e0" }} />
                </Box>

                {/* Cards */}
                <Box sx={{ bgcolor: "#fff", borderRadius: 2, overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.07)" }}>
                  {items.map((notif, idx) => {
                    const dotColor = TYPE_COLORS[notif.notificationType] || "#4b6cb7";
                    return (
                      <Box
                        key={notif.recipientId}
                        onClick={() => { if (!notif.isRead) markAsRead(notif.recipientId); }}
                        sx={{
                          display: "flex", gap: 2, px: 2.5, py: 2,
                          cursor: notif.isRead ? "default" : "pointer",
                          bgcolor: !notif.isRead ? "rgba(75,108,183,0.05)" : "#fff",
                          borderBottom: idx < items.length - 1 ? "1px solid #f0f0f0" : "none",
                          transition: "background 0.15s",
                          borderLeft: !notif.isRead ? "3px solid rgba(75,108,183,0.6)" : "3px solid transparent",
                          "&:hover": { bgcolor: !notif.isRead ? "rgba(75,108,183,0.09)" : "#fafafa" },
                        }}
                      >
                        {/* Avatar */}
                        <Box sx={{ position: "relative", flexShrink: 0 }}>
                          <Avatar sx={{
                            width: 46, height: 46, fontWeight: 700, fontSize: "1.1rem",
                            background: "linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%)",
                          }}>
                            {getInitial(notif)}
                          </Avatar>
                          <Box sx={{
                            position: "absolute", bottom: 1, right: 1,
                            width: 11, height: 11, borderRadius: "50%",
                            bgcolor: dotColor, border: "2px solid #fff",
                          }} />
                        </Box>

                        {/* Body */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.4 }}>
                            <Typography
                              variant="subtitle2"
                              fontWeight={notif.isRead ? 500 : 700}
                              color={notif.isRead ? "text.secondary" : "text.primary"}
                              sx={{ lineHeight: 1.3, flex: 1, pr: 1 }}
                            >
                              {notif.title}
                            </Typography>
                            <Typography variant="caption" color="text.disabled"
                              sx={{ whiteSpace: "nowrap", fontSize: "0.7rem", flexShrink: 0 }}>
                              {formatDate(notif.createdOn)}
                            </Typography>
                          </Box>

                          <Typography variant="body2" color="text.secondary"
                            sx={{ fontSize: "0.82rem", lineHeight: 1.5, mb: 0.8,
                              display: "-webkit-box", WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                            {notif.message}
                          </Typography>

                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                              {notif.notificationType && (
                                <Chip label={notif.notificationType} size="small"
                                  sx={{ height: 18, fontSize: "0.62rem", fontWeight: 600,
                                    bgcolor: `${dotColor}18`, color: dotColor, px: 0.5 }} />
                              )}
                              {notif.sourceModule && (
                                <Chip label={notif.sourceModule} size="small"
                                  sx={{ height: 18, fontSize: "0.62rem", fontWeight: 600,
                                    bgcolor: "rgba(0,0,0,0.05)", color: "#666", px: 0.5 }} />
                              )}
                              {notif.priority === "High" && (
                                <Chip label="High Priority" size="small"
                                  sx={{ height: 18, fontSize: "0.62rem", fontWeight: 700,
                                    bgcolor: "#fff3e0", color: "#e65100", px: 0.5 }} />
                              )}
                            </Box>
                            {!notif.isRead && (
                              <Tooltip title="Mark as read">
                                <IconButton size="small"
                                  onClick={e => { e.stopPropagation(); markAsRead(notif.recipientId); }}
                                  sx={{ color: "#4b6cb7", p: 0.5, "&:hover": { bgcolor: "rgba(75,108,183,0.1)" } }}>
                                  <MarkEmailReadIcon sx={{ fontSize: "1rem" }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })
        )}

        {loadingMore && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} sx={{ color: "#4b6cb7" }} />
          </Box>
        )}
      </Box>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={hasMore ? -1 : (pageNo - 1) * pageSize + notifications.length}
        page={pageNo - 1}
        onPageChange={handleChangePage}
        rowsPerPage={pageSize}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 20, 50, 100]}
        labelRowsPerPage="Rows per page:"
        ActionsComponent={({ page, onPageChange }) => (
          <Box sx={{ display: "flex", ml: 2 }}>
            <IconButton onClick={e => onPageChange(e, page - 1)} disabled={page === 0 || loading}>
              <NavigateBeforeIcon />
            </IconButton>
            <IconButton onClick={e => onPageChange(e, page + 1)} disabled={!hasMore || loading}>
              <NavigateNextIcon />
            </IconButton>
          </Box>
        )}
        sx={{ bgcolor: "#fff", borderRadius: 2, mt: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}
      />
    </Box>
  );
}